import React, { type FC, useCallback, useContext, useRef, useState } from "react";
import type { DefaultValues, Mode, UseFormReturn } from "react-hook-form";
import type { z } from "zod";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  type ButtonProps,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Group,
} from "../components";
import { Form, type FormDefaultSchema, FormReset, FormSubmit } from "../components/form";
import { cn } from "../utils";

type RenderFn<T> = (close: (data?: T) => void) => React.ReactNode;

interface ModalContextData {
  open<T>(render: RenderFn<T>): Promise<T | undefined>;
  close<T>(data?: T): void;
  setOptions: React.Dispatch<React.SetStateAction<ModalOptions>>;
}

const ModalContext = React.createContext<ModalContextData>({
  open: () => Promise.resolve(undefined),
  close: () => {},
  setOptions: () => {},
});

interface ModalProps {
  onClose: () => void;
  open: boolean;
  content?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  type?: "dialog" | "alert";
}

const Modal: FC<ModalProps> = ({ onClose, type = "alert", ...props }) => {
  const onOpenChange = useCallback(
    (shouldBeOpen: boolean) => {
      if (!shouldBeOpen) {
        onClose();
      }
    },
    [onClose]
  );

  return type === "dialog" ? (
    <ModalDialog {...props} onOpenChange={onOpenChange} />
  ) : (
    <ModalAlertDialog {...props} onOpenChange={onOpenChange} />
  );
};

interface ModalDialogProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  content?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

const ModalDialog: FC<ModalDialogProps> = ({ title, description, className, content, ...props }) => (
  <Dialog {...props}>
    <DialogContent className={cn(className)}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className={cn(!description && "hidden")}>{description ?? title}</DialogDescription>
      </DialogHeader>
      {content}
    </DialogContent>
  </Dialog>
);

const ModalAlertDialog: FC<ModalDialogProps> = ({ title, description, className, content, ...props }) => (
  <AlertDialog {...props}>
    <AlertDialogContent className={cn(className)}>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription className={cn(!description && "hidden")}>{description ?? title}</AlertDialogDescription>
      </AlertDialogHeader>
      {content}
    </AlertDialogContent>
  </AlertDialog>
);

interface ModalData {
  isOpen: boolean;
  content?: React.ReactNode;
}

export interface ModalOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  type?: ModalProps["type"];
}

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [{ isOpen, content }, setData] = useState<ModalData>({
    isOpen: false,
  });
  const [options, setOptions] = useState<ModalOptions>({
    title: "",
    description: "",
    className: "",
    type: "alert",
  });

  const resolver = useRef<(v?: unknown) => void>(undefined);

  const close = useCallback(function <T>(data?: T) {
    resolver.current?.(data);
    setData((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const open = useCallback(
    function <T>(fn: RenderFn<T>) {
      // Resolve any previous promises
      resolver.current?.();

      return new Promise<T | undefined>((resolve, reject) => {
        try {
          resolver.current = resolve as any;
          const content = fn(close);
          setData({ isOpen: true, content });
        } catch (error) {
          reject(error);
        }
      });
    },
    [close]
  );

  return (
    <ModalContext.Provider value={{ open, close, setOptions }}>
      <Modal onClose={close} content={content} open={isOpen} {...options} />
      {children}
    </ModalContext.Provider>
  );
};

export interface OpenModalOptions extends ModalOptions {}

export const useOpenModal = () => {
  const { open, close, setOptions } = useContext(ModalContext);

  const openModal = useCallback(
    function <T>(openFn: RenderFn<T>, options?: OpenModalOptions) {
      if (options) {
        setOptions(options);
      }

      return open(openFn);
    },
    [open, setOptions]
  );

  return { openModal, closeModal: close };
};

export interface OpenFormOptions<TSchema extends FormDefaultSchema> extends OpenModalOptions {
  mode?: Mode;
  validationSchema?: TSchema;
  defaultValues?: DefaultValues<z.infer<TSchema>>;
  /** `form` lets a submit that fails server-side report it on the still-open form (pair it with `autoClose: false`). */
  onSubmit?: (
    values: z.infer<TSchema>,
    close: () => void,
    form: UseFormReturn<z.infer<TSchema>>
  ) => unknown | Promise<unknown>;
  submitButtonProps?: ButtonProps;
  /** Extra footer actions, rendered between Cancel and the submit button. They sit outside form validation. */
  secondaryActions?: React.ReactNode;
  autoClose?: boolean;
}

export const useOpenForm = () => {
  const { open, close, setOptions } = useContext(ModalContext);

  const openForm = useCallback(
    function <TSchema extends FormDefaultSchema>(
      formFields: React.ReactNode,
      {
        validationSchema,
        defaultValues,
        onSubmit,
        submitButtonProps,
        secondaryActions,
        autoClose = true,
        type = "dialog",
        mode = "onTouched",
        ...options
      }: OpenFormOptions<TSchema>
    ) {
      // The dialog stops scrolling as a whole: the header and footer stay put and only the fields scroll.
      setOptions({ ...options, type, className: cn("flex flex-col overflow-hidden", options.className) });

      return open<z.infer<TSchema>>((close) => (
        <Form
          mode={mode}
          onSubmit={async (v, form) => {
            await onSubmit?.(v, close, form);
            return autoClose && close(v);
          }}
          onReset={() => close()}
          defaultValues={defaultValues}
          validationSchema={validationSchema}
          className="flex flex-col gap-6 min-h-0"
        >
          {/* Bleeds into the dialog padding so focus rings and the scrollbar aren't clipped. */}
          <Group className="gap-6 min-h-0 overflow-y-auto -mx-6 -my-1 px-6 py-1">{formFields}</Group>

          <AlertDialogFooter>
            <FormReset onClick={() => close()} />
            {secondaryActions}
            <FormSubmit {...submitButtonProps} />
          </AlertDialogFooter>
        </Form>
      ));
    },
    [open, setOptions]
  );

  return { openForm, closeForm: close };
};

export interface OpenConfirmationOptions extends OpenModalOptions {
  buttonProps?: ButtonProps;
}

export const useOpenConfirmation = () => {
  const { open, close, setOptions } = useContext(ModalContext);

  const openConfirmation = useCallback(
    ({ buttonProps, ...options }: OpenConfirmationOptions) => {
      setOptions(options);

      return open<boolean>((close) => (
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button theme="primary" {...buttonProps} onClick={() => close(true)}>
            {buttonProps?.children || "Confirm"}
          </Button>
        </AlertDialogFooter>
      ));
    },
    [open, setOptions]
  );

  return { openConfirmation, closeConfirmation: close };
};

export const useModal = () => ({
  ...useOpenModal(),
  ...useOpenForm(),
  ...useOpenConfirmation(),
});
